import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import apiServices from '../../services/api/index';
import {
  fetchPrimaryPoolSlots,
  savePrimaryPoolSlots,
  type PrimaryPoolSlot,
} from '../../services/dashboardSettingsService';

type PoolRow = {
  id?: number | string;
  code: string;
  name: string;
  location?: string;
};

/** code → 순번 문자열 (빈 칸 = 미지정) */
type OrderMap = Record<string, string>;

function slotsToOrderMap(slots: PrimaryPoolSlot[]): OrderMap {
  const m: OrderMap = {};
  for (const s of slots) {
    m[s.code] = String(s.order);
  }
  return m;
}

function orderMapToSlots(orderMap: OrderMap, allCodes: string[]): PrimaryPoolSlot[] {
  const slots: PrimaryPoolSlot[] = [];
  for (const code of allCodes) {
    const raw = (orderMap[code] ?? '').trim();
    if (!raw) continue;
    const order = parseInt(raw, 10);
    if (!Number.isFinite(order) || order < 1) continue;
    slots.push({ code, order });
  }
  return slots;
}

const PrimaryPoolsSettingsCard: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [allPools, setAllPools] = useState<PoolRow[]>([]);
  const [orderMap, setOrderMap] = useState<OrderMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    let slots: PrimaryPoolSlot[] = [];
    try {
      slots = await fetchPrimaryPoolSlots();
    } catch {
      enqueueSnackbar(
        '저장된 주요 시설 순번을 불러오기 실패. 목록은 표시되며 순번을 다시 지정·저장 가능.',
        { variant: 'warning' }
      );
    }

    try {
      const poolsRes = await apiServices.poolApi.getPools();
      const list =
        poolsRes.success && Array.isArray(poolsRes.data) ? poolsRes.data : [];
      setAllPools(
        list
          .filter((p) => p?.code)
          .map((p) => ({
            id: p.id,
            code: String(p.code),
            name: p.name || String(p.code),
            location: p.location,
          }))
      );
      setOrderMap(slotsToOrderMap(slots));
      if (!poolsRes.success) {
        enqueueSnackbar(
          poolsRes.message || '시설 목록을 불러오기 실패',
          { variant: 'error' }
        );
      }
    } catch {
      enqueueSnackbar('시설 목록을 불러오기 실패', { variant: 'error' });
      setAllPools([]);
      setOrderMap(slotsToOrderMap(slots));
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const nameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of allPools) m.set(p.code, p.name);
    return m;
  }, [allPools]);

  /** 입력 중에도 순번 오름차순으로 미리보기 */
  const orderedPreview = useMemo(() => {
    const slots = orderMapToSlots(
      orderMap,
      allPools.map((p) => p.code)
    ).sort((a, b) => a.order - b.order || a.code.localeCompare(b.code, 'ko'));
    return slots.map((s) => ({
      ...s,
      name: nameByCode.get(s.code) || s.code,
    }));
  }, [orderMap, allPools, nameByCode]);

  const assignedCount = orderedPreview.length;

  const setOrder = (code: string, value: string) => {
    // 자리수 제한 없음 — 양의 정수만
    if (value === '' || /^\d+$/.test(value)) {
      setOrderMap((prev) => ({ ...prev, [code]: value }));
    }
  };

  const handleSave = async () => {
    const slots = orderMapToSlots(
      orderMap,
      allPools.map((p) => p.code)
    );
    const orders = slots.map((s) => s.order);
    const dup = orders.find((o, i) => orders.indexOf(o) !== i);
    if (dup != null) {
      enqueueSnackbar(
        `표시 순번 ${dup}이(가) 중복됨. 순번은 시설마다 다르게 지정`,
        { variant: 'warning' }
      );
      return;
    }

    setSaving(true);
    try {
      await savePrimaryPoolSlots(slots);
      await load();
      enqueueSnackbar('주요 시설 순번이 저장 완료', { variant: 'success' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        '저장에 실패';
      enqueueSnackbar(String(msg), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flex: 1, minWidth: 0, pr: 1 }}
        >
          <StarIcon color="primary" fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              주요 시설
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              순번은 선택 사항. 지정한 시설이 앞에 오고, 미지정은 이름순으로 관제에 표시됨
            </Typography>
          </Box>
          {!loading && (
            <Chip
              size="small"
              label={`${assignedCount}곳 지정`}
              color={assignedCount ? 'primary' : 'default'}
            />
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            disabled={saving || loading}
            sx={{ flexShrink: 0 }}
          >
            저장
          </Button>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : allPools.length === 0 ? (
          <Alert severity="info">등록된 시설 없음. 시설 관리에서 먼저 등록</Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                순번을 비워 두면 미지정. 지정된 시설 → 미지정 시설(이름순) 순으로 관제에 표시.
                대시보드 「시설 상태」위젯에는 순번을 준 시설만 표시됨.
              </Typography>
              <Grid container spacing={1}>
                {allPools.map((pool) => (
                  <Grid item xs={12} sm={6} key={pool.code}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        minHeight: 44,
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        placeholder="—"
                        aria-label={`${pool.name} 표시 순번`}
                        value={orderMap[pool.code] ?? ''}
                        onChange={(e) => setOrder(pool.code, e.target.value)}
                        inputProps={{
                          min: 1,
                          style: { textAlign: 'center', padding: '6px 4px' },
                        }}
                        sx={{ width: 56, flexShrink: 0 }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          title={pool.name}
                          sx={{ lineHeight: 1.25 }}
                        >
                          {pool.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {pool.code}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  height: '100%',
                  bgcolor: (t) =>
                    t.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  지정 순서
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  순번이 있는 시설만 표시됨. 비워 둔 시설은 여기 없고, 관제에서는 이름순으로 뒤에 붙음.
                </Typography>
                {orderedPreview.length === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    아직 순번이 지정된 시설 없음
                  </Alert>
                ) : (
                  <List dense disablePadding>
                    {orderedPreview.map((row, idx) => (
                      <React.Fragment key={row.code}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItem disableGutters sx={{ py: 0.75, gap: 1 }}>
                          <Chip
                            size="small"
                            label={row.order}
                            color="primary"
                            sx={{ minWidth: 36, fontWeight: 700 }}
                          />
                          <ListItemText
                            primary={row.name}
                            secondary={row.code}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default PrimaryPoolsSettingsCard;
